import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { getImpersonation, stopImpersonation } from '../../utils/impersonation';

// Shown on every page while an admin acts as another user, so the two
// accounts can never be confused. "Back to my account" ends the session.
const minutesLeft = (expiresAt) => {
  if (!expiresAt) return null;
  return Math.max(0, Math.round((new Date(expiresAt).getTime() - Date.now()) / 60000));
};

function ImpersonationBanner() {
  const state = getImpersonation();
  const [left, setLeft] = useState(() => minutesLeft(state?.expiresAt));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!state?.expiresAt) return undefined;
    const t = setInterval(() => setLeft(minutesLeft(state.expiresAt)), 30000);
    return () => clearInterval(t);
  }, [state?.expiresAt]);

  // Lets the layout keep its last lines clear of the fixed bar: the body
  // class switches the extra space on, the variable carries the bar's real
  // height (it wraps onto two lines on a phone).
  const active = !!state?.adminToken;
  const barRef = useRef(null);
  useEffect(() => {
    if (!active) return undefined;
    const root = document.documentElement;
    document.body.classList.add('impersonating');
    const measure = () => {
      if (barRef.current) root.style.setProperty('--impersonation-bar-height', `${barRef.current.offsetHeight}px`);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer && barRef.current) observer.observe(barRef.current);
    return () => {
      if (observer) observer.disconnect();
      document.body.classList.remove('impersonating');
      root.style.removeProperty('--impersonation-bar-height');
    };
  }, [active]);

  if (!active) return null;

  return (
    <Box
      ref={barRef}
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 1,
        px: 2,
        py: 1,
        backgroundColor: '#b26a00',
        color: '#fff',
        // Fixed bar: the layout is a CSS grid with named areas, an extra
        // grid child would shift them.
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2000,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.4)',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        Logged in as {state.user?.email || 'another user'}
        {state.admin?.email ? ` (admin: ${state.admin.email})` : ''}
        {left !== null ? ` · ends in ${left} min` : ''}
      </Typography>
      <Button
        size="small"
        variant="contained"
        color="inherit"
        // The global button style in Layout.css sets background and margin;
        // keep the button white on the orange bar.
        sx={{ color: '#b26a00', backgroundColor: '#fff', m: 0, '&:hover': { backgroundColor: '#ffe0b2', color: '#7a4800' } }}
        disabled={busy}
        onClick={() => { setBusy(true); stopImpersonation(); }}
      >
        Back to my account
      </Button>
    </Box>
  );
}

export default ImpersonationBanner;
